import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { ScaleAlibiWeight } from '../database/schema.js';
import { zql } from '../schema.js';
import { assertCan, EventLogs } from '../utils.js';
import { itemsDetail, valueItem } from '../util/event-log-items.js';
import { addEventLog, EventLogResources } from './util.js';
export const AddScaleAlibiSchema = z.object({
    memoryNumber: z.number().nonnegative().nonoptional(),
    receivedWeight: z.number().nonoptional(),
    totalWeight: z.number().optional(),
    expectedWeight: z.number().nonoptional(),
    unit: ScaleAlibiWeight.default(ScaleAlibiWeight.enum.g),
    label: z.string().nonempty().nonoptional(),
    therapySequenceId: z.uuidv7().nonoptional(),
    scaleSerialNumber: z.string().nonempty().nonoptional(),
    raw: z.string().optional(),
});
export const MarkScaleAlibiReplacedSchema = z.object({
    therapySequenceId: z.uuidv7().nonoptional(),
    memoryNumber: z.int().nonnegative().nonoptional(),
});
export const scaleAlibis = {
    add: defineMutator(AddScaleAlibiSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'production.produce');
        const now = Date.now();
        const newScaleAlibi = {
            id: uuidv7(),
            ...args,
            replaced: false,
            createdAt: now,
            createdBy: ctx.user.id,
        };
        await tx.mutate.scaleAlibis.insert(newScaleAlibi);
        if (tx.location === 'server') {
            const patches = createPatch({}, newScaleAlibi);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.scaleAlibi,
                resourceId: newScaleAlibi.id,
                action: 'create',
                domain: 'production',
                event: EventLogs.SCALE_WEIGHT_CAPTURED,
                status: 'OK',
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                details: {
                    label: args.label,
                    ...itemsDetail([
                        ...valueItem('measured', args.receivedWeight, { unit: args.unit }),
                        ...valueItem('expected', args.expectedWeight, { unit: args.unit }),
                        ...valueItem('memory_number', args.memoryNumber),
                    ]),
                },
            }, ctx);
        }
    }),
    markReplaced: defineMutator(MarkScaleAlibiReplacedSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'production.produce');
        if (tx.location !== 'server')
            return;
        const alibi = await tx.run(zql.scaleAlibis
            .where('therapySequenceId', '=', args.therapySequenceId)
            .where('memoryNumber', '=', args.memoryNumber)
            .where('replaced', '=', false)
            .orderBy('createdAt', 'desc')
            .one());
        if (!alibi)
            return;
        const now = Date.now();
        await tx.mutate.scaleAlibis.update({
            id: alibi.id,
            replaced: true,
            updatedAt: now,
            updatedBy: ctx.user.id,
        });
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.scaleAlibi,
            resourceId: alibi.id,
            action: 'update',
            domain: 'production',
            patches: createPatch({ replaced: false }, { replaced: true }),
            performedBy: ctx.user.id,
            performedAt: now,
        }, ctx);
    }),
};
//# sourceMappingURL=scale-alibi.mutators.js.map